import { AddOrEditIntegrationDrawerFooter } from '@modules/integrationsV2/components/AddOrEditIntegrationDrawerFooter';
import { AddOrEditIntegrationDrawerHeader } from '@modules/integrationsV2/components/AddOrEditIntegrationDrawerHeader';
import { IntegrationDescriptionForm } from '@modules/integrationsV2/components/IntegrationDescriptionForm';
import {
  useUpdateIntegration,
  useValidateIntegrationForm,
} from '@modules/integrationsV2/hooks';
import {
  getAddIntegrationDrawerHeaderTitle,
  trackIntegrationEvent,
} from '@modules/integrationsV2/utils';
import { GetAddIntegrationFormDrawerContentProps } from '@modules/integrationsV2/utils/getAddIntegrationFormDrawerContent';
import { Box, Stack, TextField } from '@mui/material';
import {
  IntegrationChannelType,
  IntegrationDirection,
} from 'generated/graphql';
import { ChangeEvent, useState } from 'react';
import useCreateIntegrationEdgeServer from 'v2/core/repository/edge-server/integrations';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { useGetUser } from 'v2/core/services/user';

export const FreshdeskFormDrawerContent = ({
  integrationType,
  integrationChannel,
  onDrawerClose,
  integrationID,
}: GetAddIntegrationFormDrawerContentProps) => {
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();

  const [domain, setDomain] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [descriptionInput, setDescriptionInput] = useState('');

  const { createIntegration, status: createIntegrationStatus } =
    useCreateIntegrationEdgeServer({
      tenantID,
      direction: IntegrationDirection.Inbound,
      channel: IntegrationChannelType.FreshDesk,
      integrationType,
      createdByUser: userID,
      inputData: {
        freshDesk: {
          apiKey,
          domain,
        },
      },
      description: descriptionInput.trim(),
      onCompleted: () => {
        onDrawerClose();
        trackIntegrationEvent({
          eventName: 'Integration Added',
          integrationChannelType: IntegrationChannelType.FreshDesk,
        });
      },
    });

  const [updateIntegration, { status: updateIntegrationStatus }] =
    useUpdateIntegration({
      action: 'reauth',
      channelType: IntegrationChannelType.FreshDesk,
      integrationType,
      onCompleted: () => {
        onDrawerClose();
      },
    });

  const isReauthorizeForm = !!integrationID;

  const [
    submitForm,
    {
      isDisabled,
      isDescriptionRequired,
      isValidatingDescription,
      descriptionValidForQuestions,
      validatedDescriptionMessage,
      isPreValidatedDescription,
    },
  ] = useValidateIntegrationForm({
    shouldValidateDescription: !isReauthorizeForm,
    channelType: IntegrationChannelType.FreshDesk,
    integrationType,
    hasValidFormFields: isReauthorizeForm ? !!apiKey : !!domain && !!apiKey,
    descriptionInput,
    currentDescription: '',
    onValidated: async () => {
      if (isReauthorizeForm) {
        await updateIntegration({
          integrationID,
          isActive: true,
          updateAuthData: {
            freshDesk: {
              apiKey,
            },
          },
        });
      } else {
        await createIntegration();
      }
    },
  });

  const handleDomainInputField = (event: ChangeEvent<HTMLInputElement>) => {
    setDomain(event.target.value);
  };

  const handleAPIKeyInputField = (event: ChangeEvent<HTMLInputElement>) => {
    setApiKey(event.target.value);
  };

  const isLoading =
    IsLoading(createIntegrationStatus) || IsLoading(updateIntegrationStatus);

  return (
    <Stack height="100%" justifyContent="space-between">
      <Box>
        <AddOrEditIntegrationDrawerHeader
          integrationChannel={integrationChannel}
          integrationType={integrationType}
          title={getAddIntegrationDrawerHeaderTitle(
            integrationChannel,
            integrationType,
          )}
          isReauthorizeForm={isReauthorizeForm}
        />
        <Stack p={3} spacing={4}>
          <Stack spacing={2}>
            {!isReauthorizeForm && (
              <TextField
                required
                fullWidth
                label="Domain"
                placeholder="Enter Domain"
                variant="outlined"
                onChange={handleDomainInputField}
                disabled={isLoading}
              />
            )}
            <TextField
              required
              fullWidth
              label="API Key"
              placeholder="Enter API key"
              variant="outlined"
              onChange={handleAPIKeyInputField}
              disabled={isLoading}
            />
          </Stack>
          {!isReauthorizeForm && (
            <IntegrationDescriptionForm
              value={descriptionInput}
              onChange={setDescriptionInput}
              isRequired={isDescriptionRequired}
              validatedDescriptionMessage={validatedDescriptionMessage}
              isValidationInProgress={isValidatingDescription}
              descriptionValidForQuestions={descriptionValidForQuestions}
              isPreValidatedDescription={isPreValidatedDescription}
              integrationType={integrationType}
            />
          )}
        </Stack>
      </Box>
      <AddOrEditIntegrationDrawerFooter
        onCancel={onDrawerClose}
        onSubmit={submitForm}
        buttonText={isReauthorizeForm ? 'Reauthorize' : 'Integrate'}
        isDisabled={isDisabled}
        isLoading={isLoading}
      />
    </Stack>
  );
};
